import { DialogDisclosure, Form, Heading, VisuallyHidden } from "@ariakit/react";
import {
  DialogTrigger,
  DownloadLink,
  FormSubmitButton,
  getSubmitFailureMessage,
  LoadingIndicator,
  ModalDialog,
  SubmitErrorAlert,
  trackEvent,
  useForm,
  useGetProductStatus,
  type BuyParams,
} from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "wouter";
// import { hrefs } from "../App/hrefs";
import { RecipeVariants } from "@vanilla-extract/recipes";
import { client } from "../client";
import { KnownErrorMessage } from "../db-error";
import { Typeface } from "../fonts.css";
import { DialogHeader } from "../form-dialog";
import { ViewContainer } from "../max-width-container";
import { MobileNavLayout } from "../mobile-nav-layout";
import { FathomEvent } from "../paragraphs/fathom";
import { hrefs } from "../root-layout/fmt";
import { button, buttonV2 } from "../utils.css";
import { FeaturesOverview } from "./FeaturesOverview";
import coverImageUrl from "./hobgoblinCover.jpg";
import { accountNeeded, actions, banner, downloadSection } from "./style.css";

const BUY = "Buy $19.99";
const PRODUCT_CODE = "hobgoblin";

function AccountNeededDialog() {
  return (
    <ModalDialog size="small" className={accountNeeded.dialog}>
      <div className={accountNeeded.dialogContent}>
        <DialogHeader heading="Almost there!"></DialogHeader>

        <p className={accountNeeded.info}>
          We need an account to associate with your purchase, so that you can always get the{" "}
          <em>latest version of the rules</em> and automatically unlock <em>in-app features</em>.
        </p>
        <div className={accountNeeded.actions}>
          <Link
            href={hrefs.join({
              redirectTo: hrefs.buy({ note: "continuePurchase" }),
              backTo: hrefs.buy(),
            })}
            className={buttonV2({ theme: "black" })}
          >
            Join
          </Link>

          <Link
            href={hrefs.login({
              redirectTo: hrefs.buy({ note: "continuePurchase" }),
              backTo: hrefs.buy(),
            })}
            className={buttonV2({})}
          >
            Log in
          </Link>
        </div>
      </div>
    </ModalDialog>
  );
}

function BuyButton(props: Pick<NonNullable<RecipeVariants<typeof buttonV2>>, "size">) {
  const { size = "large" } = props;

  const { form, submitName } = useForm({
    defaultValues: {},

    async onSubmit() {
      const result = await client.startCheckoutSession({
        products: [{ code: PRODUCT_CODE, quantity: 1 }],
        successUrl: `${window.location.origin}/checkout/sessions/{CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.toString(),
      });

      if (result.isFailure) {
        return result.mapFailure((failure) => getSubmitFailureMessage(failure));
      }

      return result;
    },

    onSuccess(value) {
      // Full redirect to redirect URL.
      window.location.assign(value.redirectTo);
    },
  });

  return (
    <Form store={form} style={{ display: "contents" }}>
      <FormSubmitButton
        loading={<LoadingIndicator />}
        className={buttonV2({ size, theme: "gold" })}
        onClick={() => trackEvent(FathomEvent.BUY)}
      >
        {BUY}
      </FormSubmitButton>
      <SubmitErrorAlert name={submitName} />
    </Form>
  );
}

function ActionBanner(props: {
  matchParam: BuyParams["note"];
  heading: ReactNode;
  nudge: ReactNode;
  action: ReactNode;
}) {
  const { matchParam, heading, nudge, action } = props;
  const [params] = useSearchParams();
  const noteParamValue = params.get("note");

  if (matchParam !== noteParamValue) {
    return null;
  }

  return (
    <div className={banner.container}>
      <div className={banner.content}>
        <div>
          <div className={banner.heading}>{heading}</div>
          <div className={banner.nudge}>{nudge}</div>
        </div>

        {action}
      </div>
    </div>
  );
}

function LoginNeededBanner() {
  return (
    <ActionBanner
      matchParam="loginNeeded"
      heading="Not logged in"
      nudge="Log in to download the PDF."
      action={
        <Link
          href={hrefs.login({
            backTo: hrefs.buy({ note: "loginNeeded" }),
            redirectTo: hrefs.buy(),
          })}
          className={buttonV2({ size: "small", theme: "black" })}
        >
          Log in
        </Link>
      }
    />
  );
}

function ContinuePurchaseBanner() {
  return (
    <ActionBanner
      matchParam="continuePurchase"
      heading="Account sorted!"
      nudge="Continue to buy?"
      action={<BuyButton size="small" />}
    />
  );
}

function TryQSR() {
  return (
    <Link className={button} href={hrefs.quickStartRules()}>
      Try for Free
      <div
        style={{
          // fontFamily: font
          fontFamily: Typeface.MINION,
          fontStyle: "italic",
          textTransform: "initial",
        }}
      >
        Quick Start Rules
      </div>
    </Link>
  );
}

function AlreadyOwnNote() {
  return (
    <div>
      <Link
        href={`~/docs/help/accessing-owned-content?${new URLSearchParams({ backTo: hrefs.buy() })}`}
        className={interactiveText}
      >
        I already own Hobgoblin
      </Link>
    </div>
  );
}

function SubtleQuickStartNote() {
  return (
    <div className={actions.note}>
      Or get stuck in with the
      <br />
      <Link href={hrefs.quickStartRules()} className={interactiveText}>
        Quick Start Rules
      </Link>
      !
    </div>
  );
}

export function BookCover() {
  return (
    <img
      src={coverImageUrl}
      alt=""
      className={downloadSection.bookCover}
      width={860}
      height={1170}
    />
  );
}

export function DownloadSection(props: { action: ReactNode }) {
  return (
    <main className={downloadSection.downloadSection}>
      <VisuallyHidden>
        <Heading>Download Greathelm</Heading>
      </VisuallyHidden>

      <div className={downloadSection.bookContainer}>
        <BookCover />
      </div>

      <div className={downloadSection.action}>{props.action}</div>
    </main>
  );
}

export function RulesPage() {
  const result = useGetProductStatus(PRODUCT_CODE);
  const pageTitle = "Rules";

  if (result.isPending) {
    return (
      <MobileNavLayout title={pageTitle}>
        <ViewContainer padding="small">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minBlockSize: "max(30rem, 30svh)",
            }}
          >
            <LoadingIndicator />
          </div>
        </ViewContainer>
      </MobileNavLayout>
    );
  }

  if (result.isFailure) {
    return (
      <MobileNavLayout title={pageTitle}>
        <ViewContainer>
          <KnownErrorMessage error={result.failure} />
        </ViewContainer>
      </MobileNavLayout>
    );
  }

  if (result.value.type === "AUTHENTICATE") {
    return (
      <MobileNavLayout title={pageTitle}>
        <LoginNeededBanner />

        <ViewContainer>
          <DownloadSection
            action={
              <>
                <div className={actions.buttons}>
                  <DialogTrigger>
                    <AccountNeededDialog />
                    <DialogDisclosure className={actions.buy}>{BUY}</DialogDisclosure>
                  </DialogTrigger>

                  <TryQSR />
                </div>

                {/* <AlreadyOwnNote /> */}
              </>
            }
          />

          <FeaturesOverview />
        </ViewContainer>
      </MobileNavLayout>
    );
  }

  if (result.value.type === "PURCHASE") {
    return (
      <MobileNavLayout title={pageTitle}>
        <ContinuePurchaseBanner />

        <ViewContainer>
          <DownloadSection
            action={
              <>
                <div className={actions.buttons}>
                  <BuyButton />

                  <TryQSR />
                </div>

                {/* <AlreadyOwnNote /> */}
              </>
            }
          />

          <FeaturesOverview />
        </ViewContainer>
      </MobileNavLayout>
    );
  }

  return (
    <MobileNavLayout title={pageTitle}>
      <ViewContainer>
        <DownloadSection
          action={
            <>
              <DownloadLink className={actions.download} href={result.value.downloadUrl}>
                Download
              </DownloadLink>

              <SubtleQuickStartNote />
            </>
          }
        ></DownloadSection>
      </ViewContainer>
    </MobileNavLayout>
  );

  // return (
  //   <Page {...pageProps}>
  //     <ViewContent inlineVariant="narrow" blockVariant="shrink">
  //       <DownloadSection
  //         action={
  //           <>
  //             <DownloadButton href={result.value.downloadUrl} />
  //             <SubtleQuickStartNote />
  //           </>
  //         }
  //       />
  //     </ViewContent>
  //   </Page>
  // );
}
